import Vue from 'vue'
import logo from '@/assets/logo-192.png'
import helpers from '@/helpers'
import notificationMp3 from '@/assets/sounds/notification.mp3'
import { PracticeApi } from '@/services'
import moment from 'moment'
import * as chrono from 'chrono-node'
const parsetime = chrono.casual.clone()
parsetime.refiners.push({
  refine: (context, results) => {
    // If there is no AM/PM (meridiem) specified,
    //  let all time between 1:00 - 6:00 be PM (13.00 - 16.00)
    results.forEach(result => {
      if (
        !result.start.isCertain('meridiem') &&
        result.start.get('hour') >= 1 &&
        result.start.get('hour') < 6
      ) {
        result.start.assign('meridiem', 1)
        result.start.assign('hour', result.start.get('hour') + 12)
      }
    })
    return results
  }
})
let isWindowFocused = true

const CHAT_HISTORY_COUNT = 50
const DEFAULT_NOTIFICATION_CB = () => {
  window.focus()
}

const PageTitleNotification = {
  _vars: {
    originalTitle: document.title,
    interval: null
  },
  on: function(notification, intervalSpeed) {
    var _this = this
    _this._vars.originalTitle = document.title
    if (_this._vars.interval) clearInterval(_this._vars.interval)
    _this._vars.interval = setInterval(function() {
      document.title =
        _this._vars.originalTitle === document.title
          ? notification
          : _this._vars.originalTitle
    }, intervalSpeed || 1000)
  },
  off: function() {
    clearInterval(this._vars.interval)
    document.title = this._vars.originalTitle
  }
}
window.addEventListener(
  'focus',
  function(e) {
    PageTitleNotification.off()
    isWindowFocused = true
  },
  false
)
window.addEventListener(
  'blur',
  function(e) {
    isWindowFocused = false
  },
  false
)

function desktopNotification(title, body, callback) {
  playSound()

  if (!window.Notification) return

  if (window.Notification.permission !== 'granted')
    window.Notification.requestPermission()

  let notification = new window.Notification(title, { body, icon: logo })
  PageTitleNotification.on('New Message')
  notification.onclick =
    typeof callback === 'function' ? callback : DEFAULT_NOTIFICATION_CB
}

function playSound() {
  try {
    var audio = new Audio(notificationMp3).play()
  } catch (err) {}
}

const referralToContact = r => ({
  name: r.patient_firstname + ' ' + r.patient_lastname,
  id:
    r.patient_phone &&
    (r.patient_phone.startsWith('+') ? '+' : '+1') +
      r.patient_phone.replace(/\D/g, ''),
  rid: r.id,
  scheduled_time: r.scheduled_time
})

const DEFAULT_STATE = () => ({
  messages: {},
  loadingIds: [],
  isOpen: false,
  activeContactId: null,
  view: 'chat-list', //patient list
  filter: null
})

const state = DEFAULT_STATE()

const mutations = {
  reset(state) {
    Object.assign(state, DEFAULT_STATE())
  },
  message(state, { id, message }) {
    if (!(id in state.messages)) Vue.set(state.messages, id, {})

    if (message.temp_id && message.temp_id != message.sid) {
      Vue.delete(state.messages[id], message.temp_id)
    }
    Vue.set(state.messages[id], message.sid, message)
  },
  setIsLoading(state, { number, isLoading }) {
    const index = state.loadingIds.findIndex(u => u === number)
    if (isLoading) {
      if (index === -1) state.loadingIds.push(number)
    } else if (index !== -1) state.loadingIds.splice(index, 1)
  },
  setActiveContact(state, id) {
    state.activeContactId = id
  },
  setChatVisibility(state, { isOpen, view }) {
    state.isOpen = isOpen
    state.view = view || 'chat-list'
  },
  setView(state, view) {
    state.view = view
  },
  setFilter(state, filter) {
    state.filter = filter
  }
}

const actions = {
  async load({ state, commit, dispatch, rootState }, number) {
    commit('setIsLoading', { number, isLoading: true })
    let msgs = await PracticeApi.related(
      rootState.auth.user.practice.id,
      `sms-history${number ? '/' + number : ''}`
    )
    commit('setIsLoading', { number, isLoading: false })
    const practiceNumber = rootState.auth.user.practice.sms_number
    msgs.forEach(msg => {
      commit('message', {
        id: number || (msg.to === practiceNumber ? msg.from : msg.to),
        message: msg
      })
    })
  },
  async send({ state, commit, rootState, dispatch }, { to, msg }) {
    const temp_id = new Date().getTime()
    const pending_msg = {
      temp_id,
      body: msg,
      date_created: moment().toISOString(),
      date_sent: null,
      from: rootState.auth.user.practice.sms_number,
      sid: temp_id,
      status: 'sending',
      to
    }
    commit('message', {
      id: to,
      message: pending_msg
    })
    try {
      let response = await PracticeApi.saveRelated(
        rootState.auth.user.practice.id,
        'send-sms',
        { to, msg },
        { json: false }
      )
      const message = await response.json()
      commit('message', { id: to, message: { ...message, temp_id } })
    } catch (e) {
      commit('message', {
        id: to,
        message: { ...pending_msg, status: 'Failed to send' }
      })
    }
  },
  messageReceived({ state, dispatch, commit }, message) {
    if (
      state.activeContactId === message.from &&
      state.isOpen &&
      state.view === 'message-list'
    )
      dispatch('auth/setSmsRead', message.from, { root: true })
    commit('message', { id: message.from, message })
    desktopNotification(
      'SMS Received',
      'You received an SMS from ' + message.from
    )
  },
  async setActiveContact({ state, commit, dispatch, getters }, id) {
    commit('setActiveContact', id)
    dispatch('setChatVisibility', { isOpen: true, view: 'message-list' })
    await dispatch('load', id)
  },
  async setActiveContactByReferral({ state, commit, dispatch }, referral) {
    let contact = referralToContact(referral)
    await dispatch('setActiveContact', contact.id)
  },
  setChatVisibility({ commit }, isOpen) {
    commit('setChatVisibility', isOpen)
  },
  onAuthChange: ({ commit }, { isPracticeChanged }) => {
    if (isPracticeChanged) {
      commit('reset')
    }
  },
  setFilter({ commit }, filter) {
    commit('setFilter', filter)
  }
}

const getters = {
  _messages: (state, getters, rootState) => contactId => {
    if (!(contactId in state.messages)) return []
    const readHistory = rootState.auth.user.practice.sms_read_history
    const lastMessageReadTime =
      contactId in readHistory ? parseInt(readHistory[contactId]) / 10000 : null
    const isLoading = state.loadingIds.includes(contactId)
    return Object.entries(state.messages[contactId] || {})
      .map(([k, v]) => {
        let timestamp = new Date(v.date_created).getTime()
        return {
          ...v,
          timestamp,
          isRead:
            v.from == rootState.auth.user.practice.sms_number ||
            timestamp < lastMessageReadTime,
          isLoading
        }
      })
      .sort((a, b) => a.timestamp - b.timestamp)
  },
  messages: (state, getters, rootState) => {
    if (state.view != 'message-list') return []
    let messageArray = Object.entries(
      (state.activeContactId && state.messages[state.activeContactId]) || {}
    )
      .reverse()
      .reduce((a, [k, v]) => {
        let current = {
          type: 'text',
          author:
            v.from == rootState.auth.user.practice.sms_number ? 'me' : v.from,
          data: { text: v.body, meta: moment(v.date_created).fromNow() },
          timestamp: moment(v.date_created),
          status: v.status
        }
        let prev = a.length && a[a.length - 1]
        if (prev && prev.data.meta == current.data.meta) delete prev.data.meta

        a.push(current)
        return a
      }, [])
      .reverse()
    const now = new Date()
    //todo: only if not scheduled
    const dateResults = messageArray
      .sort((a, b) => b.timestamp - a.timestamp)
      //todo:/ could reduce and pass the refDate rather than limiting
      .map((m, i) => ({
        i,
        dateparse: parsetime.parse(m.data.text, now, { forwardDate: true })
      }))
      .filter(s => s.dateparse && s.dateparse.length && s.dateparse[0].start)
      // .reduce((a,c)=> a.concat(c.dateparse), [])
      .flatMap(s => s.dateparse)
    if (dateResults && dateResults.length) {
      const firstDate = dateResults
        .sort(
          (a, b) =>
            (Object.keys(a.start.knownValues).length > 3 ? 0 : 1) -
            (Object.keys(b.start.knownValues).length > 3 ? 0 : 1)
        )[0]
        .start.date()
      const referral = rootState.referrals.referrals.find(
        s => s.id == getters.participant.rid
      )
      if (
        !referral.scheduled_time ||
        moment(referral.scheduled_time).diff(firstDate, 'hours') != 0
      ) {
        messageArray[0].suggestions = [
          {
            text: `${
              referral.scheduled_time
                ? 'Change appointment to'
                : 'Set appointment for'
            } ${helpers.calDate(firstDate)}`,
            date: firstDate,
            class: 'btn-primary btn'
          }
        ]
      } else {
        messageArray[0].suggestions = [
          {
            text: `Scheduled for ${helpers.calDate(referral.scheduled_time)}`,
            class: 'success',
            link: '/referral/schedule/' + referral.id
          }
        ]
      }
    }
    return [
      {
        author: 'support',
        type: 'text',
        id: 'info-message', // or text '1'
        data: {
          text: 'Use this chat to communicate between patients'
        },
        suggestions: [
          {
            text: `Hi, this is the office of ${rootState.auth.user.practice.name}. Feel free to text if you would like me to make an appointment or if you have any questions or concerns.`
          }
        ]
      },
      ...messageArray.sort((a, b) => a.timestamp - b.timestamp)
    ]
  },
  _contacts: (state, getters, rootState) => {
    //todo: this dedupe sugar will take the last item, need to sort by date in the event there are multiple referrals matching query
    return [
      ...new Map(
        rootState.referrals.referrals
          .filter(s => s.is_incoming)
          .map(item => [item.patient_phone?.replace(/\D/g, ''), item])
      ).values()
    ]
      .map(r => {
        let contact = referralToContact(r)
        const messages = getters._messages(contact.id)
        const latestMessage = messages.length && messages[messages.length - 1]

        return {
          ...contact,
          messages,
          latestMessage,
          unreadCount: messages.filter(m => !m.isRead).length
        }
      })
      .sort(
        (a, b) =>
          (b.latestMessage ? b.latestMessage.timestamp : 1) -
          (a.latestMessage ? a.latestMessage.timestamp : 1)
      )
  },
  contacts: (state, getters, rootState) => {
    //todo: this dedupe sugar will take the last item, need to sort by date in the event there are multiple referrals matching query
    return getters._contacts.filter(s => {
      if (!state.filter || state.filter === 'recent') return true
      if (state.filter == 'unanswered') {
        return s.latestMessage && s.latestMessage.author == s.id
      }
      if (state.filter == 'scheduled') {
        return !!s.scheduled_time
      }
    })
  },
  isVisible: (state, getters, rootState) =>
    //true ||
    rootState.auth.user.practice.sms_number && getters._contacts.length,
  // .reduce((a, c) => {
  //   a[c.id] = c
  //   return a
  // }, {})
  participant: (state, getters) =>
    getters.contacts.find(s => s.id == state.activeContactId),
  participants: getters => {
    let p = getters['participant']
    return (p && [p]) || []
  },
  unreadId: (state, getters) =>
    getters.contacts.filter(s => s.unreadCount).map(s => s.id),
  unreadTotal: (state, getters) =>
    getters.contacts.reduce((a, c) => {
      a += c.unreadCount
      return a
    }, 0)
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
